import { useCallback, useLayoutEffect, useRef } from 'react'

/* -----------------------------------------------------------------------------
 * react-native props on the DOM
 *
 * The screens still pass react-native props (accessibilityLabel, testID,
 * onLayout, pointerEvents). These helpers give them the meaning
 * react-native-web 0.17 gave them.
 * -------------------------------------------------------------------------- */

export type LayoutEvent = {
  nativeEvent: {
    layout: {
      x: number
      y: number
      width: number
      height: number
      left: number
      top: number
    }
    target: HTMLElement
  }
  timeStamp: number
}

// react-native layout sizes ignore transforms: offset sizes, client-rect origin
const measure = (
  node: HTMLElement,
  relativeTo: Element | null = node.parentElement
) => {
  const rect = node.getBoundingClientRect()
  const parent = relativeTo
    ? relativeTo.getBoundingClientRect()
    : { left: 0, top: 0 }
  return {
    x: rect.left - parent.left,
    y: rect.top - parent.top,
    width: node.offsetWidth,
    height: node.offsetHeight,
    left: rect.left,
    top: rect.top
  }
}

type PlatformNode = HTMLElement & {
  measure?: (callback: (...args: number[]) => void) => void
  measureInWindow?: (callback: (...args: number[]) => void) => void
  measureLayout?: (
    relativeTo: HTMLElement,
    onSuccess: (...args: number[]) => void
  ) => void
}

// the measuring methods react-native-web attached to host nodes; a few screens
// position popovers and pickers with them
export const attachPlatformMethods = (node: PlatformNode | null) => {
  if (!node || node.measureInWindow) return
  node.measure = callback =>
    setTimeout(() => {
      const { x, y, width, height, left, top } = measure(node)
      callback(x, y, width, height, left, top)
    }, 0)
  node.measureInWindow = callback =>
    setTimeout(() => {
      const { left, top, width, height } = measure(node, null)
      callback(left, top, width, height)
    }, 0)
  node.measureLayout = (relativeTo, onSuccess) =>
    setTimeout(() => {
      const { x, y, width, height, left, top } = measure(node, relativeTo)
      onSuccess(x, y, width, height, left, top)
    }, 0)
}

let resizeObserver: ResizeObserver | null = null
const layoutHandlers = new WeakMap<Element, (event: LayoutEvent) => void>()

const getResizeObserver = () => {
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const node = entry.target as HTMLElement
        setTimeout(() => {
          const handler = layoutHandlers.get(node)
          if (!handler) return
          handler({
            nativeEvent: { layout: measure(node), target: node },
            timeStamp: Date.now()
          })
        }, 0)
      })
    })
  }
  return resizeObserver
}

// onLayout fires once after mount and whenever the element's size changes
export const useLayoutHandler = (onLayout?: (event: LayoutEvent) => void) => {
  const nodeRef = useRef<HTMLElement | null>(null)

  useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node || !onLayout) return
    layoutHandlers.set(node, onLayout)
  }, [onLayout])

  const hasHandler = !!onLayout
  useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node || !hasHandler) return
    const observer = getResizeObserver()
    observer.observe(node)
    return () => {
      observer.unobserve(node)
      layoutHandlers.delete(node)
    }
  }, [hasHandler])

  return nodeRef
}

export const mergeRefs =
  <T>(...refs: (React.Ref<T> | undefined)[]) =>
  (value: T) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') ref(value)
      else if (ref) (ref as React.MutableRefObject<T>).current = value
    })
  }

export const useMergedRef = <T>(...refs: (React.Ref<T> | undefined)[]) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useCallback(mergeRefs(...refs), refs)

/* ---- props ----------------------------------------------------------------- */

// accessibilityRole values react-native-web rendered as their own element
const roleElements: Record<string, string> = {
  article: 'article',
  banner: 'header',
  blockquote: 'blockquote',
  code: 'code',
  complementary: 'aside',
  contentinfo: 'footer',
  deletion: 'del',
  emphasis: 'em',
  figure: 'figure',
  insertion: 'ins',
  form: 'form',
  label: 'label',
  list: 'ul',
  listitem: 'li',
  main: 'main',
  navigation: 'nav',
  region: 'section',
  strong: 'strong'
}

const roleElement = (role?: string, level?: number) =>
  role === 'heading' ? `h${level ?? 1}` : role ? roleElements[role] : undefined

export type RNDOMProps = {
  accessibilityLabel?: string
  accessibilityRole?: string
  accessibilityLevel?: number
  accessibilityHint?: string
  accessibilityLiveRegion?: 'none' | 'polite' | 'assertive'
  accessibilityState?: Record<string, any>
  accessible?: boolean
  testID?: string
  nativeID?: string
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only'
  focusable?: boolean
  hitSlop?: unknown
  collapsable?: boolean
  importantForAccessibility?: string
  dataSet?: Record<string, string>
}

// the only props react-native-web 0.17 forwarded to the DOM, besides any aria-*
// prop; the rest (data-*, className, tabIndex, unknown props) were dropped, so
// the kit drops them too
const FORWARDED = new Set([
  'children',
  'dir',
  'lang',
  'href',
  'onBlur',
  'onFocus',
  'onClick',
  'onClickCapture',
  'onContextMenu',
  'onKeyDown',
  'onKeyDownCapture',
  'onKeyUp',
  'onKeyUpCapture',
  'onMouseDown',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseMove',
  'onMouseOver',
  'onMouseOut',
  'onMouseUp',
  'onPointerDown',
  'onPointerMove',
  'onPointerUp',
  'onPointerEnter',
  'onPointerLeave',
  'onTouchCancel',
  'onTouchCancelCapture',
  'onTouchEnd',
  'onTouchEndCapture',
  'onTouchMove',
  'onTouchMoveCapture',
  'onTouchStart',
  'onTouchStartCapture',
  'onScroll',
  'onWheel'
])

// react-native-web's classic resets for interactive and list elements
const INTERACTIVE_RESET = {
  backgroundColor: 'transparent',
  color: 'inherit',
  font: 'inherit',
  listStyle: 'none',
  margin: 0,
  textAlign: 'inherit',
  textDecorationLine: 'none'
}

export const roleBaseStyle = (props: Record<string, any>, tag: string) => {
  const role = props.accessibilityRole
  const element = roleElement(role, props.accessibilityLevel) || tag
  const disabled =
    props.accessibilityState?.disabled || props.accessibilityDisabled
  return {
    ...(['a', 'button', 'li', 'ul'].includes(element) || role === 'heading'
      ? INTERACTIVE_RESET
      : {}),
    ...((role === 'button' || role === 'link') && !disabled
      ? { cursor: 'pointer' }
      : {})
  }
}

const FOCUSABLE_ROLES = new Set([
  'button',
  'checkbox',
  'link',
  'radio',
  'textbox',
  'switch'
])

export const toDOMProps = (props: Record<string, any>) => {
  const {
    accessibilityLabel,
    accessibilityRole,
    accessibilityLevel,
    accessibilityHint,
    accessibilityLiveRegion,
    accessibilityState,
    accessible,
    testID,
    nativeID,
    pointerEvents,
    focusable,
    hitSlop,
    collapsable,
    importantForAccessibility,
    dataSet,
    ...rest
  } = props

  const domProps: Record<string, any> = {}
  Object.keys(rest).forEach(key => {
    if (FORWARDED.has(key) || key.startsWith('aria-')) domProps[key] = rest[key]
  })
  let element: string | undefined
  if (accessibilityLabel != null) domProps['aria-label'] = accessibilityLabel
  if (accessibilityLiveRegion != null)
    domProps['aria-live'] =
      accessibilityLiveRegion === 'none' ? 'off' : accessibilityLiveRegion
  if (accessibilityState?.disabled) domProps['aria-disabled'] = true
  if (accessibilityState?.selected != null)
    domProps['aria-selected'] = accessibilityState.selected
  if (accessibilityState?.checked != null)
    domProps['aria-checked'] = accessibilityState.checked
  if (accessibilityState?.expanded != null)
    domProps['aria-expanded'] = accessibilityState.expanded
  if (testID != null) domProps['data-testid'] = testID
  if (nativeID != null) domProps.id = nativeID
  if (dataSet)
    Object.entries(dataSet).forEach(
      ([key, value]) => (domProps[`data-${key}`] = value)
    )
  if (accessibilityRole) {
    element = roleElement(accessibilityRole, accessibilityLevel)
    // 'label' has no ARIA role: react-native-web rendered a bare <label>
    if (accessibilityRole !== 'label')
      domProps.role =
        accessibilityRole === 'none' ? 'presentation' : accessibilityRole
  }

  // tab stops, as react-native-web decided them
  const isFocusable = focusable ?? accessible
  if (isFocusable === false) {
    domProps.tabIndex = -1
  } else if (FOCUSABLE_ROLES.has(accessibilityRole)) {
    domProps.tabIndex = 0
  } else if (isFocusable === true) {
    domProps.tabIndex = 0
  }

  // pointerEvents: box-none lets children receive events but not the element
  const pointerStyle =
    pointerEvents === 'none' || pointerEvents === 'box-none'
      ? { pointerEvents: 'none' }
      : pointerEvents === 'box-only' || pointerEvents === 'auto'
      ? { pointerEvents: 'auto' }
      : {}
  const pointerChildren =
    pointerEvents === 'box-none'
      ? { '& > *': { pointerEvents: 'auto' } }
      : pointerEvents === 'box-only'
      ? { '& > *': { pointerEvents: 'none' } }
      : {}

  return { domProps, element, pointerStyle, pointerChildren }
}
